import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  useUploadProfileImage,
  useUploadProfileBackground,
  useDeleteFile,
} from '../../hooks/use-upload';
import { Loader2, Upload, X, AlertTriangle, Check, ExternalLink, Shield } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../../utils/axios-config';
import { API_ENDPOINTS } from '../../config/api-config';
import { MEDIA_DEFAULTS } from '../../config/media-defaults';

// Passport Portal URL for profile editing
const PASSPORT_PORTAL_URL = import.meta.env.VITE_PASSPORT_URL || 'https://auth.tracverse.com';

export function EditProfileModal({ isOpen, onClose, user, onSave }) {
  // Check for admin-imposed profile restrictions
  const profileRestrictions = user?.moderation?.profileRestrictions || {};
  const isAvatarRestricted = profileRestrictions.avatarRestricted || false;
  const isBackgroundRestricted =
    profileRestrictions.backgroundRestricted || false;
  const isBioRestricted = profileRestrictions.bioRestricted || false;
  const isUsernameRestricted = profileRestrictions.usernameRestricted || false;

  // Store original data to compare against for change detection
  const [originalData, setOriginalData] = useState(null);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    username: user?.username || '',
    bio: user?.bio || '',
    avatar: user?.avatar || '',
    backgroundImage: user?.backgroundImage || '',
    tagline: user?.tagline || '',
    missionStatement: user?.missionStatement || '',
  });

  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || '');
  const [backgroundPreview, setBackgroundPreview] = useState(
    user?.backgroundImage || ''
  );
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedAvatarUrl, setUploadedAvatarUrl] = useState(null);
  const [uploadedBackgroundUrl, setUploadedBackgroundUrl] = useState(null);
  const [filesToDelete, setFilesToDelete] = useState([]);
  // Track newly uploaded files that should be deleted if user cancels/removes them
  const [pendingUploadsToDelete, setPendingUploadsToDelete] = useState([]);
  // Username validation state
  const [usernameStatus, setUsernameStatus] = useState('idle'); // 'idle' | 'checking' | 'available' | 'taken' | 'invalid'
  const [usernameError, setUsernameError] = useState('');

  // Reset state when modal opens or user changes
  useEffect(() => {
    if (isOpen && user) {
      const initial = {
        name: user?.name || '',
        username: user?.username || '',
        bio: user?.bio || '',
        avatar: user?.avatar || '',
        backgroundImage: user?.backgroundImage || '',
        tagline: user?.tagline || '',
        missionStatement: user?.missionStatement || '',
      };
      setOriginalData(initial);
      setFormData(initial);
      setAvatarPreview(user?.avatar || '');
      setBackgroundPreview(user?.backgroundImage || '');
      setUploadedAvatarUrl(null);
      setUploadedBackgroundUrl(null);
      setFilesToDelete([]);
      setPendingUploadsToDelete([]);
      setIsUploading(false);
      setUploadProgress(0);
      setUsernameStatus('idle');
      setUsernameError('');
    }
  }, [isOpen, user]);

  // Debounced username availability check
  const checkUsernameAvailability = useCallback(
    async (username) => {
      // Skip if username is same as original or empty
      if (!username || username === originalData?.username) {
        setUsernameStatus('idle');
        setUsernameError('');
        return;
      }

      // Basic validation
      if (username.length < 3) {
        setUsernameStatus('invalid');
        setUsernameError('Username must be at least 3 characters');
        return;
      }

      if (username.length > 30) {
        setUsernameStatus('invalid');
        setUsernameError('Username must be less than 30 characters');
        return;
      }

      // Only allow alphanumeric, underscores, and hyphens
      if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
        setUsernameStatus('invalid');
        setUsernameError('Username can only contain letters, numbers, underscores, and hyphens');
        return;
      }

      setUsernameStatus('checking');
      setUsernameError('');

      try {
        const response = await axios.get(API_ENDPOINTS.USERS.CHECK_USERNAME(username));
        if (response.data.exists) {
          setUsernameStatus('taken');
          setUsernameError('Username is already taken');
        } else {
          setUsernameStatus('available');
          setUsernameError('');
        }
      } catch (error) {
        setUsernameStatus('idle');
        setUsernameError('');
      }
    },
    [originalData?.username]
  );

  // Debounce username check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.username !== originalData?.username) {
        checkUsernameAvailability(formData.username);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.username, originalData?.username, checkUsernameAvailability]);

  // Check if there are actual changes from original
  // Note: username, avatar, and bio are now managed in Passport Portal
  const hasChanges = useMemo(() => {
    if (!originalData) return false;
    return (
      formData.backgroundImage !== originalData.backgroundImage ||
      formData.tagline !== originalData.tagline ||
      formData.missionStatement !== originalData.missionStatement
    );
  }, [formData, originalData]);

  // Can save only when there are changes and not uploading
  const canSave = hasChanges && !isUploading;

  const uploadProfileImage = useUploadProfileImage((progress) =>
    setUploadProgress(progress)
  );
  const uploadProfileBackground = useUploadProfileBackground((progress) =>
    setUploadProgress(progress)
  );
  const deleteFile = useDeleteFile();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];

    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }

      // Store the current avatar URL before replacing (to delete after successful upload)
      const previousAvatarUrl = formData.avatar;

      setAvatarPreview(URL.createObjectURL(file));
      setIsUploading(true);

      try {
        const avatarFormData = new FormData();
        avatarFormData.append('file', file);
        const response = await uploadProfileImage.mutateAsync(avatarFormData);

        if (response.success && response.url) {
          setUploadedAvatarUrl(response.url);
          setFormData((prev) => ({ ...prev, avatar: response.url }));
          // Track this upload so we can delete it if user cancels or X's it out
          setPendingUploadsToDelete((prev) => [...prev, response.url]);

          // Delete the previous avatar from S3 (if it exists and isn't the default)
          if (previousAvatarUrl && previousAvatarUrl.includes('media.tracverse.com')) {
            const previousFileKey = previousAvatarUrl.replace('https://media.tracverse.com/', '');
            // Don't delete system default avatars
            if (!previousFileKey.includes('/assets/defaults/')) {
              try {
                await deleteFile.mutateAsync(previousFileKey);
              } catch (deleteError) {
                // Silently continue if deletion fails - not critical
                console.warn('Failed to delete previous avatar:', deleteError);
              }
            }
          }

          toast.success('Avatar uploaded successfully');
        } else {
          toast.error('Upload failed - no URL returned');
          setAvatarPreview(user?.avatar || '');
        }
      } catch (error) {
        // Check for admin restriction error
        const errorData = error.response?.data;
        if (errorData?.code === 'AVATAR_RESTRICTED') {
          toast.error(
            errorData.error ||
              'Your profile picture has been restricted by an administrator.'
          );
        } else {
          toast.error(
            `Failed to upload avatar: ${
              errorData?.error || error.message || 'Unknown error'
            }`
          );
        }
        setAvatarPreview(user?.avatar || '');
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    }
  };

  const handleBackgroundChange = async (e) => {
    const file = e.target.files?.[0];

    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be less than 10MB');
        return;
      }

      // Store the current background URL before replacing (to delete after successful upload)
      const previousBackgroundUrl = formData.backgroundImage;

      setBackgroundPreview(URL.createObjectURL(file));
      setIsUploading(true);

      try {
        const backgroundFormData = new FormData();
        backgroundFormData.append('file', file);
        const response = await uploadProfileBackground.mutateAsync(
          backgroundFormData
        );

        if (response.success && response.url) {
          setUploadedBackgroundUrl(response.url);
          setFormData((prev) => ({ ...prev, backgroundImage: response.url }));
          // Track this upload so we can delete it if user cancels or X's it out
          setPendingUploadsToDelete((prev) => [...prev, response.url]);

          // Delete the previous background from S3 (if it exists and isn't the default)
          if (previousBackgroundUrl && previousBackgroundUrl.includes('media.tracverse.com')) {
            const previousFileKey = previousBackgroundUrl.replace('https://media.tracverse.com/', '');
            // Don't delete system default backgrounds
            if (!previousFileKey.includes('/assets/defaults/')) {
              try {
                await deleteFile.mutateAsync(previousFileKey);
              } catch (deleteError) {
                // Silently continue if deletion fails - not critical
                console.warn('Failed to delete previous background:', deleteError);
              }
            }
          }

          toast.success('Background uploaded successfully');
        } else {
          toast.error('Upload failed - no URL returned');
          setBackgroundPreview(user?.backgroundImage || '');
        }
      } catch (error) {
        // Check for admin restriction error
        const errorData = error.response?.data;
        if (errorData?.code === 'BACKGROUND_RESTRICTED') {
          toast.error(
            errorData.error ||
              'Your profile background has been restricted by an administrator.'
          );
        } else {
          toast.error(
            `Failed to upload background: ${
              errorData?.error || error.message || 'Unknown error'
            }`
          );
        }
        setBackgroundPreview(user?.backgroundImage || '');
      } finally {
        setIsUploading(false);
        setUploadProgress(0);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsUploading(true);

    try {
      // Delete marked files from S3 before saving
      if (filesToDelete.length > 0) {
        for (const fileKey of filesToDelete) {
          try {
            await deleteFile.mutateAsync(fileKey);
          } catch (error) {
            // Silently continue if file deletion fails
          }
        }
      }

      // Images are already uploaded during handleAvatarChange/handleBackgroundChange
      // Just save the form data which already has the uploaded URLs
      await onSave(formData);

      // Clear uploaded URLs and delete list after successful save
      setUploadedAvatarUrl(null);
      setUploadedBackgroundUrl(null);
      setFilesToDelete([]);
      setPendingUploadsToDelete([]); // Clear - these files are now saved and should be kept

      toast.success('Profile updated successfully!');
      onClose();
    } catch (error) {
      // Handle duplicate username error
      const errorData = error.response?.data;
      if (errorData?.code === 'DUPLICATE_USERNAME') {
        toast.error('Username is already taken', {
          description: 'Please choose a different username.',
        });
      } else {
        toast.error(errorData?.error || error.message || 'Failed to update profile');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = async () => {
    // Clean up any newly uploaded files that weren't saved
    if (pendingUploadsToDelete.length > 0) {
      for (const url of pendingUploadsToDelete) {
        // Only delete if the URL is different from original (was uploaded in this session)
        const isNewUpload =
          url !== originalData?.avatar && url !== originalData?.backgroundImage;
        if (isNewUpload) {
          const fileKey = url.replace('https://media.tracverse.com/', '');
          try {
            await deleteFile.mutateAsync(fileKey);
          } catch (error) {
            // Silently continue
          }
        }
      }
    }
    // Reset state
    setFilesToDelete([]);
    setPendingUploadsToDelete([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px] bg-zinc-900 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="text-white">Edit Profile</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Manage your TalkTrail profile settings
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4 max-h-[70vh] overflow-y-auto">
            {/* Passport Portal Section - Core Identity */}
            <div className="rounded-lg border border-purple-500/30 bg-purple-500/5 p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-500/20">
                  <Shield className="h-5 w-5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-white mb-1">
                    Omniverse Passport Profile
                  </h3>
                  <p className="text-xs text-zinc-400 mb-3">
                    Your username, avatar, and bio are managed through Omniverse Passport and shared across all Tracverse apps.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {user?.username && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-800 rounded text-xs text-zinc-300">
                        @{user.username}
                      </span>
                    )}
                    {user?.bio && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-800 rounded text-xs text-zinc-400 max-w-[200px] truncate">
                        {user.bio}
                      </span>
                    )}
                  </div>
                  <a
                    href={`${PASSPORT_PORTAL_URL}/dashboard/profile`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-md transition-colors"
                  >
                    <Shield className="h-4 w-4" />
                    Edit in Passport
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-zinc-900 px-2 text-zinc-500">TalkTrail Settings</span>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="background" className="text-zinc-300">
                Background Image
              </Label>
              {isBackgroundRestricted && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-md">
                  <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0" />
                  <p className="text-xs text-red-400">
                    Your profile background has been restricted by an
                    administrator. You cannot change it until the restriction is
                    lifted.
                  </p>
                </div>
              )}
              {backgroundPreview && !isBackgroundRestricted && (
                <div className="relative w-full h-32 rounded-lg overflow-hidden border-2 border-white/10">
                  <img
                    src={backgroundPreview}
                    alt="Background preview"
                    loading="lazy"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Try local fallback if CDN fails
                      if (e.target.src !== MEDIA_DEFAULTS.LOCAL_PROFILE_BACKGROUND) {
                        e.target.src = MEDIA_DEFAULTS.LOCAL_PROFILE_BACKGROUND;
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      // If we uploaded a new image in this session, delete it from S3 immediately
                      if (uploadedBackgroundUrl) {
                        const fileKey = uploadedBackgroundUrl.replace(
                          'https://media.tracverse.com/',
                          ''
                        );
                        try {
                          await deleteFile.mutateAsync(fileKey);
                        } catch (error) {
                          // Silently continue
                        }
                        // Remove from pending uploads
                        setPendingUploadsToDelete((prev) =>
                          prev.filter((url) => url !== uploadedBackgroundUrl)
                        );
                        setUploadedBackgroundUrl(null);
                      }

                      // If user has an existing background (original), mark it for deletion on save
                      if (originalData?.backgroundImage) {
                        const fileKey = originalData.backgroundImage.replace(
                          'https://media.tracverse.com/',
                          ''
                        );
                        setFilesToDelete((prev) => [...prev, fileKey]);
                      }

                      // Clear the background completely
                      setBackgroundPreview('');
                      setFormData((prev) => ({
                        ...prev,
                        backgroundImage: '',
                      }));
                    }}
                    className="absolute top-2 right-2 bg-black/70 rounded-full p-1 hover:bg-black"
                  >
                    <X className="h-3 w-3 text-white" />
                  </button>
                </div>
              )}
              {!isBackgroundRestricted && (
                <div className="flex gap-2">
                  <label htmlFor="background-upload" className="flex-1">
                    <div className="flex items-center justify-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 border border-white/10 rounded-md cursor-pointer transition-colors">
                      <Upload className="h-4 w-4 text-zinc-400" />
                      <span className="text-sm text-zinc-300">
                        Upload Background
                      </span>
                    </div>
                    <input
                      id="background-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleBackgroundChange}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
              {!isBackgroundRestricted && (
                <p className="text-xs text-zinc-500">
                  Max 10MB. Recommended: 1920x400px
                </p>
              )}
            </div>
            {/* Only show org fields if user.type === 'organization' */}
            {user?.type === 'organization' && (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="tagline" className="text-zinc-300">
                    Tagline (for organizations)
                  </Label>
                  <Input
                    id="tagline"
                    name="tagline"
                    value={formData.tagline}
                    onChange={handleChange}
                    placeholder="Your organization's tagline"
                    className="bg-black border-white/10 text-white placeholder:text-zinc-600 focus:border-purple-500"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="missionStatement" className="text-zinc-300">
                    Mission Statement (for organizations)
                  </Label>
                  <Textarea
                    id="missionStatement"
                    name="missionStatement"
                    value={formData.missionStatement}
                    onChange={handleChange}
                    placeholder="Your organization's mission"
                    rows={3}
                    className="bg-black border-white/10 text-white placeholder:text-zinc-600 focus:border-purple-500"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isUploading}
              className="border-white/10 text-zinc-300 hover:text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isUploading || !canSave}
              className="bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {uploadProgress > 0
                    ? `Uploading ${Math.round(uploadProgress)}%`
                    : 'Saving...'}
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
